import React, { useRef, useState, useEffect } from 'react'
import { HiClipboardList, HiShare, HiPencil, HiCheck, HiX, HiCurrencyDollar } from 'react-icons/hi'
import { createPortal } from 'react-dom'
import { toast } from 'react-hot-toast'
import RealtimeIndicator from './RealtimeIndicator'
import CurrencySelector from './CurrencySelector'
import logger from '../../utils/logger'

function PollHeader({
  poll,
  pollId,
  choices,
  realtimeStatus,
  activeViewers,
  deviceId,
  onCopyLink,
  onReconnect,
  onRefresh,
  // Edit mode props
  editMode,
  editingPollTitle,
  editingTitleText,
  setEditingTitleText,
  isUpdating,
  canEditPoll,
  isPollCreator,
  hasEditableChoices,
  onStartEditingTitle,
  onCancelEditingTitle,
  onSaveTitle,
  onToggleEditMode,
  // Currency props
  currency,
  currencyConfirmed,
  onCurrencyChange,
  onCurrencyConfirm
}) {
  const [showDeviceIdTooltip, setShowDeviceIdTooltip] = useState(false)
  const [deviceIdTooltipPosition] = useState({ top: 0, left: 0 })
  const deviceIdTooltipTimeoutRef = useRef(null)

  const copyPollCode = async () => {
    try {
      await navigator.clipboard.writeText(pollId)
      toast.success('Poll code copied to clipboard!')
      logger.info('Poll code copied to clipboard', { pollId })
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = pollId
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        toast.success('Poll code copied to clipboard!')
        logger.info('Poll code copied to clipboard (fallback)', { pollId })
      } catch (fallbackErr) {
        toast.error('Failed to copy poll code. Please copy manually.')
        logger.error('Failed to copy poll code', { error: fallbackErr })
      }
      document.body.removeChild(textArea)
    }
  }







  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (deviceIdTooltipTimeoutRef.current) {
        clearTimeout(deviceIdTooltipTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="card fade-in-up" style={{ padding: '1rem 1.5rem', marginBottom: '1.5rem', position: 'relative', zIndex: editMode ? 50 : 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Title Row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', width: '100%' }}>
          <HiClipboardList style={{ fontSize: '2rem', color: 'var(--accent-primary)', flexShrink: 0, marginTop: '0.25rem' }} />
          {editingPollTitle ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={editingTitleText}
                onChange={(e) => setEditingTitleText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveTitle()
                  if (e.key === 'Escape') onCancelEditingTitle()
                }}
                disabled={isUpdating}
                autoFocus
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '0.5rem 0.75rem',
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  background: 'var(--bg-secondary)',
                  border: '2px solid var(--accent-primary)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text-primary)',
                  outline: 'none'
                }}
              />
              <button
                className="btn btn-primary"
                onClick={onSaveTitle}
                disabled={isUpdating}
                style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', minWidth: '36px', height: '36px' }}
                aria-label="Save title"
              >
                <HiCheck style={{ fontSize: '1.2rem' }} />
              </button>
              <button
                className="btn btn-secondary"
                onClick={onCancelEditingTitle}
                disabled={isUpdating}
                style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', minWidth: '36px', height: '36px' }}
                aria-label="Cancel editing"
              >
                <HiX style={{ fontSize: '1.2rem' }} />
              </button>
            </div>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
              <h2 style={{
                margin: 0,
                lineHeight: '1.3',
                fontSize: '1.5rem',
                flex: 1,
                minWidth: 0,
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                wordBreak: 'normal',
                whiteSpace: 'normal'
              }}>{poll?.title}</h2>
              {editMode && canEditPoll && (
                <button
                  className="btn btn-secondary"
                  onClick={onStartEditingTitle}
                  disabled={isUpdating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    width: '40px',
                    height: '36px',
                    minWidth: '40px',
                    minHeight: '36px',
                    flexShrink: 0,
                    borderRadius: 'var(--radius-md)',
                    boxSizing: 'border-box'
                  }}
                  aria-label="Edit poll title"
                >
                  <HiPencil style={{ fontSize: '1rem' }} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Currency Selector - compact inline, only in edit mode for poll owner */}
        {isPollCreator && editMode && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-color)',
            position: 'relative',
            zIndex: 100
          }}>
            <HiCurrencyDollar style={{ fontSize: '1rem', color: 'var(--accent-primary)', flexShrink: 0 }} />
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Currency:</span>
            <CurrencySelector
              value={currency}
              onChange={onCurrencyChange}
              disabled={isUpdating}
            />
          </div>
        )}

        {/* Bottom Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
          width: '100%',
          paddingTop: '0.5rem',
          borderTop: '1px solid var(--border-color)'
        }}>
          {/* Bottom Left: Code and Live Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap', flexShrink: 0 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0 0.625rem',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-color)',
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              whiteSpace: 'nowrap',
              height: '36px',
              minHeight: '36px',
              boxSizing: 'border-box'
            }}>
              <span>Code:</span>
              <code
                onClick={copyPollCode}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)'
                  e.currentTarget.style.backgroundColor = 'var(--border-color)'
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)'
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.borderColor = 'transparent'
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)'
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)'
                }}
                style={{
                  backgroundColor: 'transparent',
                  padding: '0.0625rem 0.1875rem',
                  borderRadius: 'var(--radius-sm)',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  letterSpacing: '0.1em',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all var(--transition-base)',
                  boxShadow: 'none',
                  display: 'inline-block',
                  border: '1px solid transparent'
                }}
              >{pollId}</code>
            </div>
            <RealtimeIndicator
              status={realtimeStatus}
              activeViewers={activeViewers}
              choices={choices}
              deviceId={deviceId}
              onReconnect={onReconnect}
              onRefresh={onRefresh}
            />
          </div>

          {/* Bottom Right: Action Buttons (Icon Only) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            {/* Edit Mode Toggle - only show if user is poll creator or has any editable choices */}
            {(isPollCreator || hasEditableChoices) && (
              <button
                className={`btn ${editMode ? 'btn-primary' : 'btn-secondary'}`}
                onClick={onToggleEditMode}
                aria-label={editMode ? 'Exit edit mode' : 'Enter edit mode'}
                title={editMode ? 'Exit edit mode' : 'Enter edit mode'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '36px',
                  minHeight: '36px',
                  padding: 0,
                  borderRadius: 'var(--radius-md)',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s ease'
                }}
              >
                <HiPencil style={{ fontSize: '1rem' }} />
              </button>
            )}
            <button
              className="btn btn-secondary"
              onClick={onCopyLink}
              aria-label="Copy Link"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '36px',
                minHeight: '36px',
                padding: 0,
                borderRadius: 'var(--radius-md)',
                boxSizing: 'border-box'
              }}
            >
              <HiShare style={{ fontSize: '1rem' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Device ID Tooltip */}
      {showDeviceIdTooltip && deviceId && createPortal(
        <div
          onMouseEnter={() => {
            if (deviceIdTooltipTimeoutRef.current) {
              clearTimeout(deviceIdTooltipTimeoutRef.current)
              deviceIdTooltipTimeoutRef.current = null
            }
            setShowDeviceIdTooltip(true)
          }}
          onMouseLeave={() => {
            setShowDeviceIdTooltip(false)
          }}
          style={{
            position: 'fixed',
            top: `${deviceIdTooltipPosition.top}px`,
            left: `${deviceIdTooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            padding: '0.5rem 0.75rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            fontSize: '0.7rem',
            color: 'var(--text-primary)',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap',
            zIndex: 10001,
            pointerEvents: 'none',
            animation: 'tooltipFadeIn 0.2s ease-out'
          }}
        >
          {deviceId}
        </div>,
        document.body
      )}
    </div>
  )
}

export default PollHeader
